// Ce qu'on affiche quand l'API refuse.
//
// **Pourquoi ce fichier existe.** Tous les écrans écrivent la même ligne —
// `err?.response?.data?.detail` — et la posent dans un bandeau. Ça marche
// pour les refus que la plateforme écrit elle-même, qui sont des phrases.
//
// Mais un **422 de FastAPI** ne porte pas une phrase : son `detail` est une
// **liste d'objets** (`[{loc, msg, type}, …]`). Posée telle quelle dans du
// JSX, elle lève « Objects are not valid as a React child » — et comme le
// bandeau est monté à la racine de l'application, c'est **tout l'écran qui
// blanchit**. Le premier dépôt d'une feuille d'émargement s'est fait refuser
// en 422 et a blanchi l'écran : le refus était juste, c'est son affichage qui
// a cassé.
//
// Une seule fonction lit donc ces formes, et le bandeau l'appelle pour tout ce
// qu'il affiche : un objet qui passerait demain ne casse plus rien.

/** Le nom du champ en cause, quand le refus le dit (`['body', 'file']`). */
const champ = (loc) =>
  Array.isArray(loc) ? loc.filter((x) => x !== 'body').slice(-1)[0] : null;

/** Rend lisible n'importe quoi qu'on voudrait afficher comme texte. */
export function texteLisible(valeur) {
  if (valeur === null || valeur === undefined) return '';
  if (typeof valeur === 'string') return valeur;
  if (typeof valeur === 'number' || typeof valeur === 'boolean') return String(valeur);
  if (Array.isArray(valeur)) {
    return valeur.map(texteLisible).filter(Boolean).join(' · ');
  }
  if (typeof valeur === 'object') {
    // La forme d'un refus de validation : on nomme le champ et ce qui cloche.
    const msg = valeur.msg || valeur.detail || valeur.message;
    if (msg) {
      const c = champ(valeur.loc);
      return c ? `${c} : ${texteLisible(msg)}` : texteLisible(msg);
    }
    try {
      return JSON.stringify(valeur);
    } catch {
      return String(valeur);
    }
  }
  return String(valeur);
}

/**
 * La phrase à montrer pour une erreur d'appel.
 *
 * `defaut` est ce qu'on dit quand l'API n'explique rien — une panne de réseau,
 * une 500 muette : mieux vaut une phrase de l'écran que « undefined ».
 */
export function messageErreur(err, defaut = "L'opération a échoué.") {
  const brut = err?.response?.data?.detail ?? err?.response?.data?.message;
  const texte = texteLisible(brut).trim();
  return texte || err?.message || defaut;
}
